import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useMemo } from "react";
import {
  Loader2,
  Plus,
  Package,
  X,
  PlusCircle,
  Check,
  Search,
  Minus,
  Wrench,
  PackagePlus,
  Percent,
  Tag,
  Lock,
} from "lucide-react";
import { CreateProductModal } from "@/components/products/CreateProductModal";
import { ProductsProvider } from "@/components/products/ProductsContext";
import { productCategoryService } from "@/services/product-category.service";
import { ProductCategory } from "@/types/product";
import { useToast } from "@/hooks/use-toast";
import {
  AddItemWithoutRulesData,
  AddItemWithRulesData,
} from "@/services/opportunityService";
import { Product as ServiceProduct } from "@/services/product.service";
import { Badge } from "@/components/ui/badge";
import { useEffect } from "react";

export interface Product extends ServiceProduct {
  price?: number;
}

const formatDisplayValue = (value: string | number) => {
  if (value === undefined || value === null) return "";
  const strValue = value.toString();
  if (!strValue) return "";
  const [integer, decimal] = strValue.split(".");
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal !== undefined
    ? `${formattedInteger}.${decimal}`
    : formattedInteger;
};

const formatCurrency = (value: number) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (data: AddItemWithRulesData & { id?: string }) => void;
  allProducts: Product[];
  isLoading?: boolean;
  initialData?: {
    id?: string;
    product_id: string;
    quantity: number;
    base_price: number;
    includes_installation?: boolean;
    applied_rules: { rule_id: string; value: string }[];
    notes?: string;
  } | null;
  slug: string;
  onRefreshProducts?: () => void;
}

export function AddProductModal({
  isOpen,
  onClose,
  onAddProduct,
  allProducts,
  isLoading,
  initialData,
  slug,
  onRefreshProducts,
}: AddProductModalProps) {
  const { toast } = useToast();
  const [selectedProductId, setSelectedProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [basePrice, setBasePrice] = useState<string>("0");
  const [productNotes, setProductNotes] = useState<string>("");
  const [customRules, setCustomRules] = useState<
    {
      id: string;
      rule_name: string;
      description: string;
      price_modifier: string;
      price_type: "fixed" | "per_unit" | "percentage";
    }[]
  >([]);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [requiredRuleIds, setRequiredRuleIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [discountValue, setDiscountValue] = useState<string>("0");
  const [discountType, setDiscountType] = useState<"percentage" | "fixed">(
    "percentage",
  );

  useEffect(() => {
    if (isOpen && slug) {
      productCategoryService
        .getProductCategories({ slug, is_active: true })
        .then((res) => {
          const mapped = (res || []).map((cat) => ({
            id: cat.id,
            name: cat.name,
            description: cat.description || "",
            color: cat.color || "#3b82f6",
            createdAt: cat.created_at,
            updatedAt: cat.updated_at,
          }));
          setCategories(mapped);
        })
        .catch((err) => console.error("Error loading categories", err));
    }
  }, [isOpen, slug]);

  // Synchronize state with initialData when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setSelectedProductId(initialData.product_id);
        setQuantity(initialData.quantity.toString());
        setBasePrice(initialData.base_price.toString());

        // Separar reglas de catálogo de reglas personalizadas
        const catalogRuleIds: string[] = [];
        const customItems: any[] = [];
        let loadedDiscountValue = "0";
        let loadedDiscountType: "percentage" | "fixed" = "percentage";

        initialData.applied_rules.forEach((r: any) => {
          if (
            r.is_custom &&
            (r.rule_name === "Descuento Especial" ||
              r.rule_name === "Descuento")
          ) {
            loadedDiscountValue = Math.abs(
              parseFloat(r.price_modifier || "0"),
            ).toString();
            loadedDiscountType =
              r.price_type === "percentage" ? "percentage" : "fixed";
          } else if (r.is_custom) {
            customItems.push({
              id: r.rule_id || `custom-${Math.random()}`,
              rule_name: r.rule_name || "Servicio Adicional",
              description: r.description || "",
              price_modifier: (
                r.price_modifier ||
                r.calculated_amount ||
                "0"
              ).toString(),
              price_type: r.price_type || "fixed",
            } as any);
          } else {
            catalogRuleIds.push(r.rule_id);
          }
        });

        // Combine catalog rule IDs with required rule IDs from the product
        const combinedRuleIds = new Set<string>(catalogRuleIds);
        // Load product and merge its rule IDs
        const product = allProducts.find(
          (p) => p.id === initialData.product_id,
        );
        console.log("Initial product loaded:", product);
        setSelectedProduct(product || null);
        console.log("Product rules loaded:", product?.rules);
        // Store required rule IDs for UI enforcement
        const requiredRuleIds =
          product?.rules
            ?.filter((r) => {
              const val = (r as any).is_required;
              return val === true || val === "true" || val === 1 || val === "1";
            })
            .map((r) => r.id) || [];
        console.log("Initial requiredRuleIds set:", requiredRuleIds);
        setRequiredRuleIds(requiredRuleIds);
        // Ensure required rules are pre‑selected
        requiredRuleIds.forEach((id) => combinedRuleIds.add(id));
        // Set final selected rules (required rules pre‑selected)
        setSelectedRules(Array.from(combinedRuleIds));
        console.log(
          "Selected rules after init (combined):",
          Array.from(combinedRuleIds),
        );
        // Set other state values
        setCustomRules(customItems);
        setDiscountValue(loadedDiscountValue);
        setDiscountType(loadedDiscountType);
        setProductNotes((initialData as any).notes || "");
      } else {
        resetForm();
      }
    }
  }, [isOpen, initialData, allProducts]);

  // Log when requiredRuleIds change
  useEffect(() => {
    console.log("requiredRuleIds updated (debug):", requiredRuleIds);
  }, [requiredRuleIds]);

  // Log when selectedRules change (already have one, add extra info)
  useEffect(() => {
    console.log("selectedRules updated (debug):", selectedRules);
  }, [selectedRules]);

  // Ensure any required rule IDs are always present in selectedRules
  useEffect(() => {
    if (requiredRuleIds.length > 0) {
      setSelectedRules((prev) =>
        Array.from(new Set([...prev, ...requiredRuleIds])),
      );
    }
  }, [requiredRuleIds]);

  // Log whenever selectedProduct changes
  useEffect(() => {
    console.log("selectedProduct updated:", selectedProduct);
  }, [selectedProduct]);

  // Ensure cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProductId) {
      toast({
        title: "Error",
        description: "Por favor selecciona un producto",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const appliedRules = [
        ...(selectedProduct?.rules
          ?.filter((r) => selectedRules.includes(r.id))
          .map((r) => {
            const qty = parseInt(quantity) || 1;
            const bPrice =
              parseFloat(basePrice.toString().replace(/,/g, "")) || 0;
            const modifier = parseFloat(r.price_modifier.toString());
            let calcAmount = 0;
            if (
              (r.price_type as string) === "fixed" ||
              (r.price_type as string) === "per_unit"
            )
              calcAmount = modifier * qty;
            else if ((r.price_type as string) === "percentage")
              calcAmount = modifier * qty;
            else if ((r.price_type as string) === "percentage")
              calcAmount = (bPrice * qty * modifier) / 100;

            return {
              rule_id: r.id,
              rule_name: r.rule_name,
              price_modifier: r.price_modifier.toString(),
              price_type: r.price_type,
              is_required: r.is_required,
              calculated_amount: calcAmount.toString(),
              value: r.price_modifier.toString(),
            };
          }) || []),
        ...customRules.map((cr) => ({
          rule_id: cr.id,
          rule_name: cr.rule_name,
          description: cr.description,
          price_modifier: cr.price_modifier,
          price_type: cr.price_type,
          is_custom: true,
          value:
            (cr.price_type as string) === "per_unit"
              ? (parseInt(quantity) || 1).toString()
              : "1",
        })),
      ];

      const discVal = parseFloat(discountValue.replace(/,/g, "")) || 0;
      if (discVal > 0) {
        appliedRules.push({
          rule_id: `discount-${Date.now()}`,
          rule_name: "Descuento",
          description: "",
          price_modifier: (-Math.abs(discVal)).toString(),
          price_type: discountType,
          is_custom: true,
          value: "1",
        } as any);
      }

      const productData: AddItemWithRulesData & {
        id?: string;
        notes?: string;
      } = {
        id: initialData?.id,
        product_id: selectedProductId,
        quantity: parseInt(quantity) || 1,
        opportunity_id: "",
        base_price: parseFloat(basePrice.toString().replace(/,/g, "")),
        applied_rules: appliedRules as any,
        notes: productNotes || undefined,
      };

      console.log(
        "🚀 FRONTEND: Preparando datos del producto para guardar:",
        productData,
      );
      onAddProduct(productData);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al agregar producto",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedProductId("");
    setQuantity("1");
    setSelectedProduct(null);
    setSelectedRules([]);
    setRequiredRuleIds([]);
    setSearchQuery("");
    setBasePrice("0");
    setCustomRules([]);
    setDiscountValue("0");
    setDiscountType("percentage");
    setProductNotes("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleProductChange = (productId: string) => {
    setSelectedProductId(productId);
    const product = allProducts.find((p) => p.id === productId);
    setSelectedProduct(product || null);

    if (product) {
      const price = getProductPrice(product);
      setBasePrice(price.toString());

      if (product.rules) {
        const requiredRules = product.rules
          .filter((r) => {
            const val = (r as any).is_required;
            return val === true || val === "true" || val === 1 || val === "1";
          })
          .map((r) => r.id);
        console.log("Product change requiredRuleIds (debug):", requiredRules);
        setRequiredRuleIds(requiredRules);
        // Merge required rules with any already selected optional rules
        setSelectedRules((prev) =>
          Array.from(new Set([...prev, ...requiredRules])),
        );
      } else {
        setSelectedRules([]);
        setRequiredRuleIds([]);
      }
    } else {
      setBasePrice("0");
      setSelectedRules([]);
    }
  };

  const toggleRule = (ruleId: string, isRequired: boolean) => {
    // Prevent toggling of required rules (both from UI flag and stored required IDs)
    if (isRequired || requiredRuleIds.includes(ruleId)) {
      console.log("Attempted to toggle a required rule, ignored:", ruleId);
      return;
    }
    console.log(
      "Toggling rule:",
      ruleId,
      "Current selectedRules:",
      selectedRules,
    );
    setSelectedRules((prev) =>
      prev.includes(ruleId)
        ? prev.filter((id) => id !== ruleId)
        : [...prev, ruleId],
    );
  };

  const addCustomService = () => {
    setCustomRules((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        rule_name: "Nuevo Servicio",
        description: "",
        price_modifier: "0",
        price_type: "fixed" as const,
      },
    ]);
  };

  const updateCustomService = (id: string, field: string, value: any) => {
    setCustomRules((prev) =>
      prev.map((cr) => (cr.id === id ? { ...cr, [field]: value } : cr)),
    );
  };

  const removeCustomService = (id: string) => {
    setCustomRules((prev) => prev.filter((cr) => cr.id !== id));
  };

  const getProductPrice = (product: Product): number => {
    if (product.price !== undefined) return product.price;
    if (typeof product.base_price === "string") {
      return parseFloat(product.base_price) || 0;
    }
    return product.base_price || 0;
  };

  const totalPrice = useMemo(() => {
    let base = parseFloat(basePrice.toString().replace(/,/g, "")) || 0;
    const qty = parseInt(quantity) || 0;
    let extras = 0;

    // 1. Reglas de Catálogo (acumulativas sobre la base)
    selectedProduct?.rules?.forEach((rule) => {
      if (selectedRules.includes(rule.id)) {
        const modifier = parseFloat(rule.price_modifier.toString());
        if (rule.price_type === "percentage") {
          extras += (base * qty * modifier) / 100;
        } else if ((rule.price_type as string) === "per_unit") {
          extras += modifier * qty;
        } else {
          extras += modifier;
        }
      }
    });

    // 2. Servicios Personalizados (acumulativas sobre la base)
    customRules.forEach((cr) => {
      const val =
        parseFloat(cr.price_modifier.toString().replace(/,/g, "")) || 0;
      if (cr.price_type === "percentage") extras += base * qty * (val / 100);
      else if (cr.price_type === "per_unit") extras += val * qty;
      else extras += val;
    });

    const subtotalBeforeDiscount = base * qty + extras;

    // 3. Descuento al producto (APLICADO SOBRE EL NETO ACUMULADO)
    const discVal = parseFloat(discountValue.replace(/,/g, "")) || 0;
    let finalDiscountAmount = 0;
    if (discVal > 0) {
      if (discountType === "percentage") {
        finalDiscountAmount = (subtotalBeforeDiscount * discVal) / 100;
      } else {
        finalDiscountAmount = discVal;
      }
    }

    return subtotalBeforeDiscount - finalDiscountAmount;
  }, [
    basePrice,
    quantity,
    selectedRules,
    selectedProduct,
    customRules,
    discountValue,
    discountType,
  ]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter(
      (p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [allProducts, searchQuery]);

  const submitButtonText = initialData
    ? "Actualizar Producto"
    : "Agregar a la Oportunidad";
  const modalTitle = initialData
    ? "Editar Producto"
    : "Agregar Producto al Catálogo";
  const rules = selectedProduct?.rules || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden bg-white border-0 shadow-2xl rounded-[24px] max-h-[90vh] flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
              <Package className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-slate-800 tracking-tight">
                {modalTitle}
              </DialogTitle>
              <DialogDescription className="text-slate-500 mt-0.5 text-xs font-medium">
                {initialData
                  ? "Modifica la cantidad, reglas o precio base de este producto."
                  : "Selecciona un producto del catálogo para añadirlo a esta oportunidad."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8 space-y-6">
              {!selectedProduct ? (
                <div className="space-y-2">
                  <Label htmlFor="product-search" className="text-sm">
                    Buscar Producto *
                  </Label>
                  <div className="relative flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="product-search"
                        placeholder="Buscar por nombre o SKU..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={isLoading}
                        className="pl-10 h-10"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 gap-2 border-green-200 text-green-700 hover:bg-green-50"
                      onClick={() => setShowCreateProductModal(true)}
                    >
                      <PackagePlus className="h-4 w-4" />
                      Nuevo Producto
                    </Button>
                  </div>
                  <div className="border rounded-md max-h-80 overflow-y-auto">
                    {isLoading ? (
                      <div className="flex items-center justify-center p-4 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span>Cargando productos...</span>
                      </div>
                    ) : filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handleProductChange(product.id)}
                          className={`p-3 cursor-pointer border-b last:border-b-0 flex justify-between items-start ${
                            selectedProductId === product.id
                              ? "bg-blue-50"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {product.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              SKU: {product.sku}
                            </p>
                          </div>
                          <div className="text-right ml-4 flex-shrink-0">
                            <p className="font-semibold text-green-600 text-sm">
                              Q{formatCurrency(getProductPrice(product))}
                            </p>
                            {product.category?.name && (
                              <Badge
                                variant="outline"
                                className="text-xs h-5 mt-1"
                              >
                                {product.category.name}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No se encontraron productos.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-xs text-slate-500 uppercase tracking-wider">
                        Producto Seleccionado
                      </h4>
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-blue-600 hover:text-blue-700"
                        onClick={() => {
                          setSelectedProductId("");
                          setSelectedProduct(null);
                          setSelectedRules([]);
                          setSearchQuery("");
                        }}
                      >
                        Cambiar
                      </Button>
                    </div>
                    <div className="mb-2.5">
                      <p className="text-base font-bold text-slate-800">
                        {selectedProduct.name}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs text-slate-600">
                          Precio unitario:
                        </span>
                        <p className="text-sm font-semibold text-green-600 mt-0.5">
                          Q{formatCurrency(getProductPrice(selectedProduct))}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-600">SKU:</span>
                        <p className="text-sm font-medium mt-0.5">
                          {selectedProduct.sku}
                        </p>
                      </div>
                    </div>
                  </div>

                  {rules.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-blue-900">
                        Ajustes de Catálogo
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        {rules.map((rule) => {
                          const isRequired = !!rule.is_required;
                          const isSelected = selectedRules.includes(rule.id);
                          return (
                            <div
                              key={rule.id}
                              className={`flex items-center justify-between p-2 rounded border transition-colors ${
                                isRequired
                                  ? "border-amber-200 bg-amber-50 cursor-not-allowed"
                                  : isSelected
                                    ? "bg-blue-50 border-blue-200 cursor-pointer"
                                    : "border-slate-200 hover:bg-slate-50 cursor-pointer"
                              }`}
                              onClick={
                                isRequired
                                  ? undefined
                                  : () => toggleRule(rule.id, isRequired)
                              }
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <div
                                  className={`h-4 w-4 rounded border flex items-center justify-center ${
                                    isRequired
                                      ? "bg-amber-400 border-amber-400"
                                      : isSelected
                                        ? "bg-blue-600 border-blue-600"
                                        : "border-slate-300 bg-white"
                                  }`}
                                >
                                  {(isRequired || isSelected) && (
                                    <Check className="h-3 w-3 text-white" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-medium text-xs truncate">
                                      {rule.rule_name}
                                    </span>
                                    {isRequired && (
                                      <Badge
                                        variant="secondary"
                                        className="bg-amber-100 text-amber-800 text-[10px] h-4"
                                      >
                                        Obligatorio
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div
                                className={`text-xs font-semibold ml-2 whitespace-nowrap ${parseFloat(rule.price_modifier.toString()) >= 0 ? "text-green-600" : "text-red-600"}`}
                              >
                                {rule.price_type === "percentage"
                                  ? `${rule.price_modifier}%`
                                  : `Q${rule.price_modifier}`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 pt-3 border-t">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-bold text-blue-900 flex items-center">
                        <Wrench className="w-4 h-4 mr-1.5" /> Servicios y Extras
                        Personalizados
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 text-[10px] border-blue-200 text-blue-700"
                        onClick={addCustomService}
                      >
                        <Plus className="w-3 h-3 mr-1" /> Agregar Servicio
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {customRules.map((cr) => (
                        <div
                          key={cr.id}
                          className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2 relative group"
                        >
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-red-100 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => removeCustomService(cr.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] text-blue-700">
                                Nombre del Servicio
                              </Label>
                              <Input
                                className="h-8 text-xs bg-white"
                                placeholder="Ej: Instalación, Flete, Diseño..."
                                value={cr.rule_name}
                                onChange={(e) =>
                                  updateCustomService(
                                    cr.id,
                                    "rule_name",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] text-blue-700">
                                Tipo de Ajuste
                              </Label>
                              <Select
                                value={cr.price_type}
                                onValueChange={(val: any) =>
                                  updateCustomService(cr.id, "price_type", val)
                                }
                              >
                                <SelectTrigger className="h-8 text-xs bg-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="fixed">
                                    Fijo Total (una sola vez)
                                  </SelectItem>
                                  <SelectItem value="per_unit">
                                    Por Unidad (× cantidad)
                                  </SelectItem>
                                  <SelectItem value="percentage">
                                    Porcentaje (%)
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] text-blue-700">
                                Valor del Ajuste
                                {cr.price_type === "fixed" && (
                                  <span className="ml-1 text-slate-400 font-normal">
                                    (total único)
                                  </span>
                                )}
                                {cr.price_type === "per_unit" && (
                                  <span className="ml-1 text-slate-400 font-normal">
                                    (× {parseInt(quantity) || 1} uds)
                                  </span>
                                )}
                              </Label>
                              <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                                  <span className="text-[10px] text-slate-500 font-bold">
                                    {cr.price_type === "percentage" ? "%" : "Q"}
                                  </span>
                                </div>
                                <Input
                                  type="text"
                                  className="h-8 text-xs bg-white font-bold text-green-700 pl-5"
                                  value={formatDisplayValue(cr.price_modifier)}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /,/g,
                                      "",
                                    );
                                    const sanitized = val.replace(
                                      /[^0-9.]/g,
                                      "",
                                    );
                                    updateCustomService(
                                      cr.id,
                                      "price_modifier",
                                      sanitized,
                                    );
                                  }}
                                />
                              </div>
                              {cr.price_type === "per_unit" && (
                                <p className="text-[10px] text-amber-600">
                                  = Q
                                  {(parseFloat(cr.price_modifier) || 0) *
                                    (parseInt(quantity) || 1)}{" "}
                                  total ({cr.price_modifier} ×{" "}
                                  {parseInt(quantity) || 1})
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-[10px] text-blue-700">
                              Descripción / Detalles
                            </Label>
                            <Input
                              placeholder="Ej: Instalación en altura, materiales incluidos..."
                              className="h-8 text-xs bg-white"
                              value={cr.description}
                              onChange={(e) =>
                                updateCustomService(
                                  cr.id,
                                  "description",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      ))}
                      {customRules.length === 0 && (
                        <div className="text-center py-4 border border-dashed rounded-lg text-slate-400 text-xs">
                          No has agregado servicios adicionales.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notas del producto — aparece cuando hay producto seleccionado */}
                  <div className="space-y-2 pt-4 border-t">
                    <Label
                      htmlFor="product-notes"
                      className="text-sm font-bold text-slate-800 flex items-center"
                    >
                      📝 Notas / Descripción del Item
                    </Label>
                    <textarea
                      id="product-notes"
                      rows={4}
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none shadow-sm"
                      placeholder="Especificaciones técnicas, medidas, color, acabados o cualquier instrucción especial para este producto..."
                      value={productNotes}
                      onChange={(e) => setProductNotes(e.target.value)}
                    />
                    <p className="text-[10px] text-slate-400 italic">
                      Estas notas aparecerán en el anexo de la cotización.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-4 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="quantity" className="text-sm">
                  Cantidad *
                </Label>
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const current = parseInt(quantity) || 1;
                      setQuantity(Math.max(1, current - 1).toString());
                    }}
                    disabled={parseInt(quantity) <= 1}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <Input
                    id="quantity"
                    type="text"
                    min="1"
                    value={quantity}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setQuantity(val);
                    }}
                    className="text-center h-8"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      const current = parseInt(quantity) || 0;
                      setQuantity((current + 1).toString());
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {selectedProduct && (
                <div className="space-y-4 pt-2 border-t">
                  <div className="space-y-2">
                    <Label
                      htmlFor="basePrice"
                      className="text-sm font-medium text-blue-900"
                    >
                      Modificar Precio Base (Opcional)
                    </Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-slate-500 sm:text-sm">Q</span>
                      </div>
                      <Input
                        id="basePrice"
                        type="text"
                        value={formatDisplayValue(basePrice)}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, "");
                          const sanitized = val.replace(/[^0-9.]/g, "");
                          setBasePrice(sanitized);
                        }}
                        className="pl-7 font-bold text-blue-900 border-blue-200 focus-visible:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Descuento al Producto */}
                  <div className="space-y-2 pt-2 border-t">
                    <Label
                      htmlFor="discountValue"
                      className="text-sm font-medium text-blue-900"
                    >
                      Descuento al Producto (Opcional)
                    </Label>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-slate-500 sm:text-xs">
                            {discountType === "percentage" ? "%" : "Q"}
                          </span>
                        </div>
                        <Input
                          id="discountValue"
                          type="text"
                          value={formatDisplayValue(discountValue)}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            const sanitized = val.replace(/[^0-9.]/g, "");
                            setDiscountValue(sanitized);
                          }}
                          className="pl-7 font-bold text-red-600 border-red-200 focus-visible:ring-red-500"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex bg-white rounded-md p-0.5 border border-slate-200">
                        <button
                          type="button"
                          onClick={() => setDiscountType("percentage")}
                          className={`p-1.5 rounded-md ${discountType === "percentage" ? "bg-red-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                        >
                          <Percent className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDiscountType("fixed")}
                          className={`p-1.5 rounded-md ${discountType === "fixed" ? "bg-red-600 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                        >
                          <Tag className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-md p-3 border border-blue-200">
                    <h4 className="font-medium text-sm mb-2 text-blue-900">
                      Resumen Final
                    </h4>
                    <div className="space-y-2">
                      {(() => {
                        const base =
                          parseFloat(basePrice.replace(/,/g, "")) || 0;
                        const qty = parseInt(quantity) || 0;

                        // Sumar reglas de catálogo al precio base antes de mostrar subtotal
                        let catalogAdjustmentsTotal = 0;
                        selectedProduct?.rules?.forEach((rule) => {
                          if (selectedRules.includes(rule.id)) {
                            const modifier = parseFloat(
                              rule.price_modifier.toString(),
                            );
                            if ((rule.price_type as string) === "percentage")
                              catalogAdjustmentsTotal +=
                                (base * qty * modifier) / 100;
                            else if ((rule.price_type as string) === "per_unit")
                              catalogAdjustmentsTotal += modifier * qty;
                            else catalogAdjustmentsTotal += modifier;
                          }
                        });

                        const adjustedProductSubtotal =
                          base * qty + catalogAdjustmentsTotal;
                        let runningSubtotal = adjustedProductSubtotal;
                        const displayElements: React.ReactNode[] = [];

                        displayElements.push(
                          <div
                            key="base-sub"
                            className="flex justify-between items-center text-xs"
                          >
                            <span className="text-blue-900 font-bold">
                              Subtotal Producto:
                            </span>
                            <span className="font-bold">
                              Q{formatCurrency(adjustedProductSubtotal)}
                            </span>
                          </div>,
                        );

                        // Renderizar servicios personalizados (en cascada)
                        customRules.forEach((cr) => {
                          const val =
                            parseFloat(cr.price_modifier.replace(/,/g, "")) ||
                            0;
                          let amt = 0;
                          if ((cr.price_type as string) === "percentage")
                            amt = (runningSubtotal * val) / 100;
                          else if ((cr.price_type as string) === "per_unit")
                            amt = val * qty;
                          else amt = val;

                          displayElements.push(
                            <div
                              key={cr.id}
                              className="flex justify-between items-center text-xs"
                            >
                              <span className="text-blue-700 truncate max-w-[120px]">
                                {cr.rule_name}:
                              </span>
                              <span
                                className={`font-semibold ${amt >= 0 ? "text-green-700" : "text-red-600"}`}
                              >
                                {amt >= 0 ? "+" : ""}Q{formatCurrency(amt)}
                              </span>
                            </div>,
                          );
                          runningSubtotal += amt;
                        });

                        // Calcular descuento final sobre el subtotal ya acumulado
                        const discVal =
                          parseFloat(discountValue.replace(/,/g, "")) || 0;
                        let discAmt = 0;
                        if (discVal > 0) {
                          if (discountType === "percentage")
                            discAmt = (runningSubtotal * discVal) / 100;
                          else discAmt = discVal;
                        }

                        return (
                          <>
                            {displayElements}
                            {discAmt > 0 && (
                              <div className="flex justify-between items-center text-xs text-red-600">
                                <span className="text-red-700 font-medium">
                                  Descuento de Producto:
                                </span>
                                <span className="font-semibold whitespace-nowrap">
                                  -Q{formatCurrency(discAmt)}
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div className="pt-2 border-t border-blue-300 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-bold text-blue-900">
                            Total:
                          </span>
                          <span className="text-lg font-bold text-blue-700">
                            Q{formatCurrency(totalPrice)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          <div className="p-4 bg-white border-t border-slate-100 flex items-center justify-end gap-3 flex-shrink-0 relative z-20">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-5 h-9 rounded-lg text-slate-600 font-medium text-sm"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-slate-900 hover:bg-black text-white px-5 h-9 rounded-lg shadow-sm font-medium text-sm"
              disabled={!selectedProductId || isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              {submitButtonText}
            </Button>
          </div>
        </form>
      </DialogContent>

      <ProductsProvider>
        <CreateProductModal
          isOpen={showCreateProductModal}
          onClose={() => setShowCreateProductModal(false)}
          categories={categories}
          onSave={async (newProduct) => {
            if (onRefreshProducts) {
              onRefreshProducts();
            }
            // Seleccionar automáticamente el producto recién creado
            if (newProduct && newProduct.id) {
              handleProductChange(newProduct.id);
            }
            setShowCreateProductModal(false);
            toast({
              title: "Producto creado",
              description: `El producto "${newProduct.name}" ha sido creado y seleccionado.`,
            });
          }}
        />
      </ProductsProvider>
    </Dialog>
  );
}
